import { useContext, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import type { InspectionTemplateBody } from '@ayumia/contracts';
import { applyRowAction, evaluateFormCondition, formAggregates, listSearchMatches, listTabs, placementOf, rowActionState, tabRowIndexes } from '@ayumia/component-runtime';
import type { ApiClient } from '../../api/client';
import { text } from '../../app/i18n';
import { useReferenceLabels, type ReferenceLabels } from '../../views/reference-labels';
import { AppServicesContext } from '../../views/ViewContext';
import { Dialog, DialogActions } from '../../design-system/Overlay';
import { Icon } from '../../design-system/icons';
import { aggregateShown } from '../FormLayoutParts';
import { SuggestedMark, TemplateEntry } from '../TemplateEntry';
import { FormReactionsContext, useRowEntries } from '../form-reactions-context';
import { pressKey } from '../SeriesLedger';
import type { Answers, Entry, Inputs, Section } from '../form-targets';
import { optionImageUrl, useFormMedia } from './FormMedia';
import { OptionTiles } from './OptionTiles';
import { EchoName } from './PersonField';
import { CompactStepper } from './StripSection';
import { choiceOptions, columnLabel, columnSpan, emptyText, listColumns, listGrid, numberShown, keptFields, optionSourceKey, optionVisual, panelPosition, presentationOf, rowIsEmpty } from './presentation';
import { nextDraft } from '../series-ledger';

/**
 * Liste et détail (inspection-template-body 1.10.0, placement `presentation.as: list_detail` ; gabarit
 * `core.layout.strip_list_detail`) : la carte de la liste — titre, puces de compteurs (`chips`), « Ajouter »,
 * tableau aux largeurs déclarées, numéro de ligne, ligne en cours marquée, valeur manquante selon
 * `empty_display` — et, dans sa région (`entry_panel.region`), la carte de détail de la ligne en cours :
 * position (« Carcasse 1 / 1 »), champ en titre, suppression, champs sur 12 colonnes, bouton « ligne
 * suivante ». Une ligne vide ne part pas. Même parcours que la tablette. Présentation seulement : le serveur
 * relit chaque ligne.
 */

type Row = Answers;
type Template = InspectionTemplateBody;
type SeriesSection = Section & { series: NonNullable<Section['series']> };

export interface ListDetailProps {
  template: Template;
  section: SeriesSection;
  answers: Answers;
  rows: Row[];
  /** Ligne en cours (index) ; une liste vide montre une ligne nouvelle à l'index 0. */
  cursor: number;
  setCursor: (index: number) => void;
  onChange: (rows: Row[]) => void;
  disabled: boolean;
  errors: Readonly<Record<string, string>>;
  warnings: Readonly<Record<string, readonly string[]>>;
  inputs: Inputs;
}

function entryPanel(template: Template, section: Section) {
  return placementOf(template, section.section_id).entry_panel;
}

/** Ligne en cours bornée aux lignes existantes (une liste vide : la ligne 0 à venir). */
export function cursorOf(rows: readonly Row[], cursor: number): number {
  return rows.length === 0 ? 0 : Math.max(0, Math.min(cursor, rows.length - 1));
}

function visibleEntries(section: Section, row: Row): Entry[] {
  return section.fields.filter((entry) => entry.compute === undefined && evaluateFormCondition(entry.visible_when, row));
}

function requiredIn(entry: Entry, row: Row): boolean {
  return entry.field.required === true || (entry.required_when !== undefined && evaluateFormCondition(entry.required_when, row));
}

function missingOf(section: Section, row: Row): string[] {
  return visibleEntries(section, row)
    .filter((entry) => requiredIn(entry, row))
    .map((entry) => entry.field.field_id)
    .filter((id) => row[id] === undefined || row[id] === null || row[id] === '');
}

/** Visuel d'une valeur de choix dans une cellule ou un titre : image (ou « ? »), pastille, icône. */
function ValueBadge({ entry, value, size }: { entry: Entry; value: unknown; size: number }) {
  const media = useFormMedia();
  const visual = optionVisual(entry);
  if (visual !== 'image' && visual !== 'object_image') return null;
  const option = value === null || value === undefined ? undefined : choiceOptions(entry).find((candidate) => candidate.value === String(value));
  const url = option === undefined ? undefined : optionImageUrl(media, option.image, visual === 'object_image' ? optionSourceKey(entry) : undefined, option.value);
  return (
    <span className="ay-fv2-thumb" style={{ width: size, height: size }} aria-hidden="true">
      {/* Sans image : les initiales de l'option choisie (« CH » pour Chamois, comme le v1) ; rien de choisi : « ? ». */}
      {url !== undefined ? <img src={url} alt="" /> : option === undefined ? '?' : referenceBadge(option.label)}
    </span>
  );
}

const NO_CLIENT = {} as ApiClient;

/**
 * Libellés des objets cités par les champs `reference` des lignes (lus sous l'autorisation de l'utilisateur) ;
 * hors application (aperçu sans services), aucun : la cellule montre « Choix enregistré », jamais l'identifiant.
 */
export function useRowReferences(entries: readonly Entry[], rows: readonly Row[]): ReferenceLabels {
  const services = useContext(AppServicesContext);
  const wanted =
    services === null
      ? []
      : entries
          .filter((entry) => entry.field.type === 'reference')
          .map((entry) => ({ type: (entry.field as { reference_type?: string }).reference_type ?? '', ids: rows.map((row) => row[entry.field.field_id]).filter((id): id is string => typeof id === 'string' && id !== '') }));
  return useReferenceLabels(services?.client ?? NO_CLIENT, wanted);
}

/** Pastille d'un objet cité : deux lettres du dernier mot (« #1111 - Chamois » → « CH »). */
export function referenceBadge(label: string): string {
  const word = label.split(/[\s—-]+/).filter((part) => /\p{L}/u.test(part)).at(-1);
  return word === undefined ? '?' : [...word].slice(0, 2).join('').toUpperCase();
}

/** Texte affiché d'une cellule, pour la recherche (`search`, 1.14.0) : libellé d'un choix ou d'un objet, nombre et unité, texte. */
export function cellText(entry: Entry, value: unknown, labels: ReferenceLabels): string {
  if (value === undefined || value === null || value === '') return '';
  if (entry.field.type === 'enumeration' || entry.field.type === 'boolean') return choiceOptions(entry).find((option) => option.value === String(value))?.label ?? String(value);
  if (entry.field.type === 'reference') return labels.get(String(value)) ?? '';
  if (typeof value === 'number') return `${numberShown(value, presentationOf(entry).decimals)} ${(entry.field as { unit?: string }).unit ?? ''}`.trim();
  return String(value);
}

function Cell({ entry, value, tone, labels }: { entry: Entry; value: unknown; tone: string | undefined; labels: ReferenceLabels }) {
  const missing = value === undefined || value === null || value === '';
  const visual = optionVisual(entry);
  const presentation = presentationOf(entry);
  if (entry.field.type === 'reference' && !missing) {
    const label = labels.get(String(value)) ?? 'Choix enregistré';
    return (
      <span className="ay-fv2-cell__ref">
        <span className="ay-fv2-cell__badge" aria-hidden="true">
          {referenceBadge(label)}
        </span>
        <span className="ay-fv2-cell__strong">{label}</span>
      </span>
    );
  }
  if (entry.field.type === 'enumeration' || entry.field.type === 'boolean') {
    const option = missing ? undefined : choiceOptions(entry).find((candidate) => candidate.value === String(value));
    const empty = emptyText(entry, 'cell');
    if (visual === 'dot') {
      return (
        <span className={`ay-fv2-statepill${option === undefined ? ' ay-fv2-statepill--empty' : ''}`}>
          <span className={`ay-fv2-dot ay-fv2-dot--${option?.color ?? 'neutral'}`} aria-hidden="true" />
          {option?.label ?? empty.text}
        </span>
      );
    }
    return (
      <span className="ay-fv2-cell__choice">
        <ValueBadge entry={entry} value={value} size={38} />
        <span className={option === undefined ? `ay-fv2-cell__empty ay-fv2-cell__empty--${empty.tone}` : 'ay-fv2-cell__strong'}>{option?.label ?? empty.text}</span>
      </span>
    );
  }
  if (missing) {
    const empty = emptyText(entry, 'cell');
    return <span className={`ay-fv2-cell__empty ay-fv2-cell__empty--${empty.tone}`}>{empty.text}</span>;
  }
  if (typeof value === 'number') {
    const unit = (entry.field as { unit?: string }).unit;
    return <span className={tone === undefined ? undefined : `ay-fv2-cell--${tone}`}>{`${numberShown(value, presentation.decimals)}${unit === undefined ? '' : ` ${unit}`}`}</span>;
  }
  return <span className={tone === undefined ? undefined : `ay-fv2-cell--${tone}`}>{String(value)}</span>;
}

/** Carte de la liste : titre, puces, « Ajouter », tableau. */
export function ListDetailList({ template, section, answers, rows, cursor, setCursor, onChange, disabled, errors, warnings }: ListDetailProps) {
  const placement = placementOf(template, section.section_id);
  const sid = section.section_id;
  const columns = listColumns(section);
  const rowNumber = placement.row_number !== false;
  // Bouton rapide d'une ligne (1.14.0, « Conforme » du v1) : une colonne de plus, à droite.
  const quick = placement.row_action;
  const grid = `${listGrid(columns, placement.column_widths ?? {}, rowNumber)}${quick === undefined ? '' : ' 7.5rem'}`;
  const current = cursorOf(rows, cursor);
  const marker = placement.current_row ?? 'both';
  // Ligne en cours (1.16.0) : la dernière ; elle n'entre dans la liste ni dans ses puces qu'une fois ajoutée
  // (ADR-146 addendum (28)).
  const pending = (placement.entry_panel as { pending_row?: unknown } | undefined)?.pending_row !== undefined;
  const pendingIndex = pending ? Math.max(rows.length - 1, 0) : -1;
  const aggregates = formAggregates(template, pending && rows.length > 0 ? { ...answers, [sid]: rows.slice(0, -1) } : answers);
  const max = (section.series as { max_count?: number }).max_count;
  const labels = useRowReferences(columns, rows);
  // Onglets et recherche (1.14.0) : un filtre d'affichage ; la réponse garde toutes les lignes.
  const tabs = listTabs(template, sid, answers);
  const [tab, setTab] = useState<string | undefined>(undefined);
  const [query, setQuery] = useState('');
  const search = placement.search;
  const searchFields = search?.fields === undefined ? columns : columns.filter((entry) => search.fields!.includes(entry.field.field_id));
  const indexes = (rows.length === 0 ? [0] : tabRowIndexes(template, sid, answers, tab).filter((index) => query.trim() === '' || listSearchMatches(searchFields.map((entry) => cellText(entry, rows[index]![entry.field.field_id], labels)), query))).filter((index) => index !== pendingIndex);
  const activeTab = tabs.find((candidate) => candidate.tab_id === tab) ?? tabs[0];
  const chipsNode = (placement.chips ?? []).map((chip) => {
          const value = aggregates.find((candidate) => candidate.aggregate_id === chip.aggregate_ref);
          if (value === undefined) return null;
          return (
            <span key={chip.aggregate_ref} className="ay-fv2-chip" title={text(value.label)} data-testid={`chip-${chip.aggregate_ref}`}>
              {chip.icon !== undefined && <Icon name={chip.icon} size={15} />}
              {value.text === '' ? (value.unit === undefined ? '0' : `0 ${value.unit}`) : aggregateShown(value)}
            </span>
          );
        });
  const addNode = placement.add_button === 'none' ? null : (
          <button type="button" className={`ay-fv2-button ${tabs.length > 0 ? "ay-fv2-button--outline" : "ay-fv2-button--filled"}`} data-testid={`list-add-${sid}`} disabled={disabled || (max !== undefined && rows.length >= max)} onClick={() => add()}>
            <Icon name="plus" size={18} />
            {text((placement as { add_label?: Readonly<Record<string, string>> }).add_label, 'fr', 'Ajouter')}
          </button>
        );
  const quickAction = (index: number): void => {
    onChange(rows.map((row, at) => (at === index ? applyRowAction(template, sid, row) : row)));
    setCursor(index);
  };
  const add = (): void => {
    const last = rows.at(-1);
    if (last !== undefined && rowIsEmpty(last, keptFields(section))) return setCursor(rows.length - 1);
    if (rows.length === 0) {
      onChange([{}]);
      return setCursor(0);
    }
    // La ligne ajoutée reprend les champs déclarés de la précédente (`keep_fields`, l'espèce : « inherit » du v1).
    onChange([...rows, nextDraft(section, last)]);
    setCursor(rows.length);
  };
  return (
    <section className="ay-fv2-card ay-fv2-list" aria-label={text(section.label, 'fr', sid)} data-testid={`list-${sid}`}>
      <header className="ay-fv2-list__head">
        {tabs.length > 0 ? (
          <div className="ay-fv2-tabs" role="tablist" aria-label="Filtre" data-testid={`list-tabs-${sid}`}>
            {tabs.map((candidate) => {
              const on = candidate.tab_id === activeTab?.tab_id;
              return (
                <button key={candidate.tab_id} type="button" role="tab" aria-selected={on} className={`ay-fv2-tabs__tab${on ? ' ay-fv2-tabs__tab--on' : ''}`} data-testid={`list-tab-${candidate.tab_id}`} onClick={() => setTab(candidate.tab_id)}>
                  {text(candidate.label, 'fr', candidate.tab_id)}
                  <span className={`ay-fv2-tabs__count${candidate.tone === undefined ? '' : ` ay-fv2-tabs__count--${candidate.tone}`}`}>{candidate.count}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <>
            <h3 className="ay-fv2-card__title">{text(section.label, 'fr', sid)}</h3>
            <EchoName target={sid} />
          </>
        )}
        {tabs.length === 0 && chipsNode}
        <span className="ay-fv2-list__spacer" />
        {search !== undefined && (
          <label className="ay-fv2-search">
            <Icon name="search" size={16} />
            <input type="search" value={query} placeholder={text(search.placeholder as Readonly<Record<string, string>> | undefined, 'fr', 'Rechercher')} aria-label="Rechercher" data-testid={`list-search-${sid}`} onChange={(event) => setQuery(event.target.value)} />
          </label>
        )}
        {tabs.length === 0 && addNode}
      </header>
      <div className="ay-fv2-table" role="table" aria-label="Lignes">
        <div className="ay-fv2-table__head" role="row" style={{ gridTemplateColumns: grid }}>
          {rowNumber && <span role="columnheader">#</span>}
          {columns.map((entry) => (
            <span key={entry.field.field_id} role="columnheader" className={entry.field.type === 'number' ? 'ay-fv2-table__num' : undefined}>
              {columnLabel(placement.column_labels, entry)}
            </span>
          ))}
          {quick !== undefined && <span role="columnheader" />}
        </div>
        <div className="ay-fv2-table__body">
          {indexes.length === 0 && <p className="ay-fv2-table__none">Aucune ligne.</p>}
          {indexes.map((index) => {
            const row: Row = rows[index] ?? {};
            const on = index === current;
            const rowError = Object.keys(errors).some((path) => path.startsWith(`answers.${sid}.${index}.`));
            const state = quick === undefined || rows.length === 0 ? 'none' : rowActionState(template, sid, row, answers);
            const pick = (): void => {
              if (!disabled) setCursor(index);
            };
            // Ligne cliquable (un `div` : le bouton rapide ne s'imbrique pas dans un bouton).
            return (
              <div
                key={index}
                role="row"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled || undefined}
                className={`ay-fv2-table__row${on ? ` ay-fv2-table__row--current ay-fv2-table__row--${marker}` : ''}${rowError ? ' ay-fv2-table__row--error' : ''}`}
                style={{ gridTemplateColumns: grid }}
                aria-current={on ? 'true' : undefined}
                data-testid={`list-row-${index}`}
                onClick={pick}
                onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                  if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    pick();
                  }
                }}
              >
                {rowNumber && <span className="ay-fv2-table__index">{index + 1}</span>}
                {columns.map((entry) => {
                  const id = entry.field.field_id;
                  const cellWarnings = warnings[`answers.${sid}.${index}.${id}`] ?? [];
                  return (
                    <span key={id} role="cell" className={`ay-fv2-table__cell${entry.field.type === 'number' ? ' ay-fv2-table__num' : ''}`} title={cellWarnings.join(' · ') || undefined}>
                      <Cell entry={entry} value={row[id]} tone={cellWarnings.length > 0 ? 'warning' : undefined} labels={labels} />
                    </span>
                  );
                })}
                {quick !== undefined && (
                  <span role="cell" className="ay-fv2-table__quick">
                    {state === 'done' && (
                      <span className="ay-fv2-quick ay-fv2-quick--done" data-testid={`list-done-${index}`}>
                        <Icon name="check" size={14} />
                        {text(quick.done_label as Readonly<Record<string, string>> | undefined, 'fr', 'OK')}
                      </span>
                    )}
                    {state === 'action' && (
                      <button
                        type="button"
                        className="ay-fv2-quick"
                        disabled={disabled}
                        data-testid={`list-quick-${index}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          quickAction(index);
                        }}
                      >
                        {quick.icon !== undefined && <Icon name={quick.icon} size={14} />}
                        {text(quick.label as Readonly<Record<string, string>>, 'fr', 'OK')}
                      </button>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {tabs.length > 0 && (chipsNode.some((node) => node !== null) || addNode !== null) && (
        <footer className="ay-fv2-list__foot">
          {chipsNode}
          <span className="ay-fv2-list__spacer" />
          {addNode}
        </footer>
      )}
      {errors[`answers.${sid}`] !== undefined && <span className="ay-field__error">{errors[`answers.${sid}`]}</span>}
    </section>
  );
}

const PAD_KEYS = ['7', '8', '9', '4', '5', '6', '1', '2', '3', ',', '0', '⌫'];

function padShown(value: unknown, decimals: number | undefined): string {
  if (typeof value === 'number') return numberShown(value, undefined) || numberShown(value, decimals);
  return typeof value === 'string' ? value : '';
}

/** Champ au pavé du détail : grande case, valeur à droite, tirets si vide, unité en suffixe. */
function PadField({ entry, value, onChange, disabled, focused, onFocus, inputRef }: { entry: Entry; value: unknown; onChange: (value: unknown) => void; disabled: boolean; focused: boolean; onFocus: () => void; inputRef: (element: HTMLInputElement | null) => void }) {
  const presentation = presentationOf(entry);
  const unit = (entry.field as { unit?: string }).unit;
  const number = entry.field.type === 'number';
  const empty = emptyText(entry);
  return (
    <span className={`ay-fv2-pad${focused ? ' ay-fv2-pad--on' : ''}`}>
      <input
        ref={inputRef}
        id={`input-${entry.field.field_id}`}
        type="text"
        inputMode={number ? 'decimal' : 'numeric'}
        autoComplete="off"
        className="ay-fv2-pad__input"
        placeholder={empty.text === '—' ? '– –' : empty.text}
        disabled={disabled}
        value={padShown(value, presentation.decimals)}
        onFocus={onFocus}
        onChange={(event) => {
          const typed = [...event.target.value].reduce((acc, char) => pressKey(entry, acc, char === '.' ? ',' : char), '');
          onChange(typed === '' ? null : number && !typed.endsWith(',') ? Number(typed.replace(',', '.')) : typed);
        }}
      />
      {unit !== undefined && <span className="ay-fv2-pad__unit">{unit}</span>}
    </span>
  );
}

/** Carte de détail de la ligne en cours. */
export function ListDetailPanel({ template, section, rows, cursor, setCursor, onChange, disabled, errors, inputs }: ListDetailProps) {
  const sid = section.section_id;
  const panel = entryPanel(template, section) ?? { region: 'detail' };
  const current = cursorOf(rows, cursor);
  const row: Row = rows[current] ?? {};
  const [focused, setFocused] = useState<string | null>(null);
  const [attention, setAttention] = useState<ReadonlySet<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const refs = useRef<Record<string, HTMLInputElement | null>>({});
  const set = (id: string, value: unknown): void => {
    const base = rows.length === 0 ? [{}] : rows;
    onChange(base.map((candidate, index) => (index === current ? { ...candidate, [id]: value } : candidate)));
    if (attention.has(id)) setAttention(new Set([...attention].filter((key) => key !== id)));
  };
  const heading = panel.heading_field === undefined ? undefined : section.fields.find((entry) => entry.field.field_id === panel.heading_field);
  const headingOption = heading === undefined ? undefined : choiceOptions(heading).find((option) => option.value === String(row[heading.field.field_id] ?? ''));
  const headingLabels = useRowReferences(heading === undefined ? [] : [heading], [row]);
  const headingValue = heading === undefined ? undefined : row[heading.field.field_id];
  const headingText =
    heading === undefined
      ? ''
      : heading.field.type === 'reference'
        ? typeof headingValue === 'string' && headingValue !== ''
          ? (headingLabels.get(headingValue) ?? 'Choix enregistré')
          : emptyText(heading).text
        : (headingOption?.label ?? (typeof headingValue === 'string' ? headingValue : emptyText(heading).text));
  const arrows = (panel as { arrows?: boolean }).arrows === true;
  const deleteLabel = text((panel as { delete_label?: Readonly<Record<string, string>> }).delete_label, 'fr', '');
  // Une ligne vide ne se supprime pas ; la ligne en cours (1.16.0) s'efface au lieu de se supprimer (addendum (28)).
  const pendingRow = (panel as { pending_row?: { clear_label?: Readonly<Record<string, string>> } }).pending_row;
  const clearLabel = text(pendingRow?.clear_label, 'fr', '');
  const clearing = pendingRow !== undefined && clearLabel !== '' && current === rows.length - 1;
  const deletable = panel.delete_button === true && !rowIsEmpty(row, keptFields(section));
  const [confirmClear, setConfirmClear] = useState(false);
  const title = text(panel.title as Readonly<Record<string, string>> | undefined, 'fr', '');
  const position = panelPosition(title, current, Math.max(rows.length, 1), panel.show_position !== false);
  // Choix et filtre rechargés par la ligne en cours (ADR-146 addendum (21)) ; marque « proposée » des suggestions.
  const rowFields = useRowEntries();
  const { origins } = useContext(FormReactionsContext);
  const entries = visibleEntries({ ...section, fields: rowFields(section.fields, sid, current) } as Section, row);
  const next = panel.next_button;
  const goNext = (): void => {
    const missing = missingOf(section, row);
    if (missing.length > 0 && !rowIsEmpty(row)) {
      setAttention(new Set(missing));
      refs.current[missing[0]!]?.focus();
      return;
    }
    if (rowIsEmpty(row)) return;
    setAttention(new Set());
    setFocused(null);
    // Ligne en cours (1.16.0) : « Ajouter » depuis une ligne déjà ajoutée ramène à la ligne en cours.
    if (pendingRow !== undefined && current + 1 < rows.length) return setCursor(rows.length - 1);
    if (current + 1 < rows.length) return setCursor(current + 1);
    if (panel.after_add === 'stay') return;
    onChange([...rows, nextDraft(section, row)]);
    setCursor(rows.length);
  };
  const clear = (): void => {
    setConfirmClear(false);
    const previous = current > 0 ? rows[current - 1] : undefined;
    const fresh = previous === undefined ? {} : nextDraft(section, previous);
    onChange(rows.map((candidate, index) => (index === current ? fresh : candidate)));
    setAttention(new Set());
  };
  const remove = (): void => {
    setConfirmDelete(false);
    const remaining = rows.filter((_, index) => index !== current);
    onChange(remaining);
    setCursor(Math.max(0, Math.min(current, remaining.length - 1)));
    setAttention(new Set());
  };
  const focusedEntry = focused === null ? undefined : entries.find((entry) => entry.field.field_id === focused);
  const press = (key: string): void => {
    if (focusedEntry === undefined) return;
    const id = focusedEntry.field.field_id;
    const typed = pressKey(focusedEntry, padShown(row[id], undefined), key);
    set(id, typed === '' ? null : focusedEntry.field.type === 'number' && !typed.endsWith(',') ? Number(typed.replace(',', '.')) : typed);
    refs.current[id]?.focus();
  };
  const body: ReactNode[] = entries.map((entry) => {
    const id = entry.field.field_id;
    const required = requiredIn(entry, row);
    const error = errors[`answers.${sid}.${current}.${id}`] ?? (attention.has(id) ? 'Obligatoire.' : undefined);
    const label = text(entry.field.label, 'fr', id);
    const style = { gridColumn: `span ${columnSpan(entry)}` } as CSSProperties;
    const labelId = `input-${sid}-${id}-label`;
    let control: ReactNode;
    const widget = presentationOf(entry).widget;
    const nonconform = (entry as { nonconformity_when?: unknown }).nonconformity_when;
    if ((entry.field.type === 'enumeration' || entry.field.type === 'boolean') && widget !== 'selector') {
      control = (
        <OptionTiles
          id={`input-${sid}-${id}`}
          entry={entry}
          value={row[id]}
          onChange={(value) => {
            setFocused(null);
            set(id, value);
          }}
          disabled={disabled}
          required={required}
          attention={attention.has(id)}
          labelledBy={labelId}
          dangerOf={nonconform === undefined ? undefined : (option) => evaluateFormCondition(nonconform as never, { ...row, [id]: option })}
        />
      );
    } else if (entry.field.type === 'number' && widget === 'stepper') {
      // Poids saisi − / + du v1 (`console-review`) : le pas-à-pas de la bande, à la hauteur des contrôles du détail.
      control = <CompactStepper entry={entry} values={row} inputs={inputs} required={required} disabled={disabled} onChange={(value) => set(id, value)} error={error} evidence={[]} onEvidence={() => undefined} subject={undefined} />;
    } else if (entry.field.type === 'number' || widget === 'keypad' || widget === 'scan_code') {
      control = <PadField entry={entry} value={row[id]} onChange={(value) => set(id, value)} disabled={disabled} focused={focused === id} onFocus={() => setFocused(id)} inputRef={(element) => { refs.current[id] = element; }} />;
    } else {
      return (
        <div key={id} className="ay-fv2-detail__field" style={style} data-field={id}>
          <TemplateEntry entry={entry} values={row} inputs={inputs} controlId={`${sid}-${id}`} path={`answers.${sid}.${current}.${id}`} required={required} disabled={disabled} onChange={(value) => set(id, value)} onFill={(filled) => Object.entries(filled).forEach(([key, value]) => set(key, value))} {...(error === undefined ? {} : { error })} />
        </div>
      );
    }
    return (
      <div key={id} className="ay-fv2-detail__field" style={style} data-field={id}>
        <span className="ay-fv2-detail__fieldhead">
          <span className="ay-fv2-detail__label" id={labelId}>
            {label}
            {required && <span className="ay-visually-hidden"> (obligatoire)</span>}
          </span>
          <EchoName target={id} />
        </span>
        {control}
        {origins[`answers.${sid}.${current}.${id}`] !== undefined && <SuggestedMark controlId={`${sid}-${id}`} />}
        {error !== undefined && <span className="ay-field__error">{error}</span>}
      </div>
    );
  });
  const pad = panel.keypad === 'embedded' && focusedEntry !== undefined;
  return (
    <section className="ay-fv2-card ay-fv2-detail" aria-label={position} data-testid={`detail-${sid}`}>
      <header className="ay-fv2-detail__head">
        {heading !== undefined && (optionVisual(heading) === 'image' || optionVisual(heading) === 'object_image') ? <ValueBadge entry={heading} value={row[heading.field.field_id]} size={46} /> : null}
        {heading !== undefined && heading.field.type === 'reference' && <span className="ay-fv2-detail__badge" aria-hidden="true">{typeof headingValue === 'string' && headingValue !== '' ? referenceBadge(headingText) : '?'}</span>}
        <span className="ay-fv2-detail__names">
          <span className="ay-fv2-detail__position" data-testid={`detail-position-${sid}`}>
            {position}
          </span>
          {heading !== undefined && <strong className="ay-fv2-detail__heading">{headingText}</strong>}
        </span>
        {arrows && (
          <>
            <button type="button" className="ay-fv2-iconbtn" aria-label="Ligne précédente" title="Précédente" data-testid={`detail-previous-${sid}`} disabled={disabled || current <= 0} onClick={() => setCursor(current - 1)}>
              <Icon name="chevron-up" size={18} />
            </button>
            <button type="button" className="ay-fv2-iconbtn" aria-label="Ligne suivante" title="Suivante" data-testid={`detail-following-${sid}`} disabled={disabled || current >= rows.length - 1} onClick={() => setCursor(current + 1)}>
              <Icon name="chevron-down" size={18} />
            </button>
          </>
        )}
        {deletable && clearing && (
          <button type="button" className="ay-fv2-button ay-fv2-button--outline ay-fv2-button--quiet" data-testid={`detail-clear-${sid}`} disabled={disabled} onClick={() => setConfirmClear(true)}>
            <Icon name="undo" size={18} />
            {clearLabel}
          </button>
        )}
        {deletable && !clearing && deleteLabel === '' && (
          <button type="button" className="ay-fv2-iconbtn ay-fv2-iconbtn--plain" aria-label="Supprimer la ligne" title="Supprimer" disabled={disabled || (rows.length === 0)} onClick={() => (rowIsEmpty(row, keptFields(section)) ? remove() : setConfirmDelete(true))}>
            <Icon name="trash" size={20} />
          </button>
        )}
      </header>
      <div className="ay-fv2-detail__body">
        <div className="ay-fv2-detail__grid">{body}</div>
        {pad && (
          <div className="ay-fv2-keys" aria-label="Pavé numérique">
            {PAD_KEYS.map((key) => (
              <button key={key} type="button" disabled={disabled} aria-label={key === '⌫' ? 'Effacer un chiffre' : key === ',' ? 'Virgule' : key} onMouseDown={(event) => event.preventDefault()} onClick={() => press(key)}>
                {key}
              </button>
            ))}
          </div>
        )}
      </div>
      {next !== undefined && (
        <footer className="ay-fv2-detail__foot">
          {deletable && !clearing && deleteLabel !== '' && (
            <button type="button" className="ay-fv2-button ay-fv2-button--outline ay-fv2-button--quiet" data-testid={`detail-delete-${sid}`} disabled={disabled || rows.length === 0} onClick={() => (rowIsEmpty(row, keptFields(section)) ? remove() : setConfirmDelete(true))}>
              {deleteLabel}
            </button>
          )}
          <button type="button" className={`ay-fv2-button ay-fv2-button--${next.appearance ?? 'filled'}${next.width === 'fit' ? '' : ' ay-fv2-button--full'}`} disabled={disabled} onClick={goNext} data-testid={`detail-next-${sid}`}>
            {next.icon !== undefined && next.icon_position === 'start' && <Icon name={next.icon} size={18} />}
            <span>{text(next.label as Readonly<Record<string, string>> | undefined, 'fr', 'Ligne suivante')}</span>
            {next.icon !== undefined && next.icon_position !== 'start' && <Icon name={next.icon} size={18} />}
          </button>
        </footer>
      )}
      {confirmClear && (
        <Dialog title="Effacer la saisie ?" open onClose={() => setConfirmClear(false)} size="sm" footer={<DialogActions destructive={{ label: clearLabel, onClick: clear }} cancel={{ label: 'Annuler', onClick: () => setConfirmClear(false) }} />}>
          <p className="ay-text">{position}</p>
        </Dialog>
      )}
      {confirmDelete && (
        <Dialog title="Supprimer la ligne ?" open onClose={() => setConfirmDelete(false)} size="sm" footer={<DialogActions destructive={{ label: 'Supprimer', onClick: remove }} cancel={{ label: 'Annuler', onClick: () => setConfirmDelete(false) }} />}>
          <p className="ay-text">{position}</p>
        </Dialog>
      )}
    </section>
  );
}
